import React from 'react';
import { useNavigate } from 'react-router-dom';

// Public page — must render without authentication. Routed in App.js RootApp
// before the withAuthenticator gate, same as MarketingHome and BillingView.

// ── Edit these three lines when the legal entity is registered ────────────────
const COMPANY   = 'REKON';            // legal entity name, e.g. "REKON Software LLC"
const CONTACT   = 'tommy@rekon.live';
const EFFECTIVE = 'July 28, 2026';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="pp">
      <style>{CSS}</style>

      <nav className="pp-nav">
        <div className="pp-nav-logo" onClick={() => navigate('/')}>REK<span>ON</span></div>
        <button className="pp-nav-back" onClick={() => navigate('/')}>← Home</button>
      </nav>

      <header className="pp-head">
        <div className="pp-head-label">Legal</div>
        <h1>Privacy Policy</h1>
        <p className="pp-eff">Effective {EFFECTIVE}</p>
      </header>

      <main className="pp-body">

        <div className="pp-summary">
          <strong>The short version.</strong> {COMPANY} sets no cookies, runs no advertising
          or analytics trackers, and does not sell or share personal information. We store
          the shop data you enter so we can run your shop's software, and nothing else.
        </div>

        <Section n="1" title="Who this policy covers">
          <p>
            {COMPANY} is service management software for heavy-duty truck repair shops. This
            policy covers two different groups of people, and our responsibilities differ for each.
          </p>
          <ul>
            <li>
              <strong>Shops that subscribe to {COMPANY}</strong> — the business, its owner, and
              its staff. For account, login, and billing information, we are the party that
              decides how the data is used (the "controller").
            </li>
            <li>
              <strong>People whose information a shop enters into {COMPANY}</strong> — the shop's
              customers, drivers, and fleet contacts. We hold this data <em>on the shop's behalf</em>
              and only act on the shop's instructions (we are a "processor" / "service provider").
              If you are one of these people and want your information corrected or removed,
              contact the repair shop you did business with. We will support their request.
            </li>
          </ul>
        </Section>

        <Section n="2" title="What we collect">
          <h3>Account and staff information</h3>
          <p>
            Name, email address, assigned role, and shop association. Passwords are handled by
            Amazon Cognito and are never visible to us. We do not require or store Social
            Security numbers or government ID numbers.
          </p>

          <h3>Shop operating data the shop enters</h3>
          <p>
            Customer company name, contact name, phone, and email; vehicle details including VIN
            and unit number; repair orders with complaints, findings, and repair notes; job photos
            uploaded by technicians; parts, labor, estimates, and invoices; payment records showing
            method, amount, and a reference such as a check number or the last four digits of a
            card. <strong>Full card numbers are never entered into or stored by {COMPANY}.</strong>
          </p>

          <h3>Signatures</h3>
          <p>
            When a customer signs a check-in form or a road service ticket on a device, we store
            that signature image as part of the job record.
          </p>

          <h3>Location</h3>
          <p>
            Two features use location, both tied to road service dispatch:
          </p>
          <ul>
            <li>
              <strong>Stranded-vehicle location.</strong> A shop can text a customer a one-time
              link. Opening that link asks the customer's browser for permission to share their
              position. Location is read once, only after the customer taps to share it, and only
              so a technician can find the vehicle. Declining the prompt sends nothing.
            </li>
            <li>
              <strong>Technician position during a road call.</strong> When dispatch is in use, a
              technician's device may report its coordinates so the shop can place them on the
              dispatch map.
            </li>
          </ul>

          <h3>Employee time and work records</h3>
          <p>
            Clock-in and clock-out times, hours attributed to jobs, and technician assignments.
            This is the shop's employment record, stored by us for the shop. Shop management can
            see it. Shops are responsible for telling their employees that time and dispatch
            location are recorded, and for following the employment and monitoring laws of their
            state.
          </p>

          <h3>Technical logs</h3>
          <p>
            Our hosting providers record standard request logs — IP address, browser user agent,
            page requested, and timestamp — which we use to keep the service running and secure.
          </p>
        </Section>

        <Section n="3" title="Cookies, tracking, and local storage">
          <div className="pp-callout">
            <strong>{COMPANY} does not use cookies.</strong> We set no cookies of our own, and we
            load no advertising networks, analytics services, tracking pixels, or session-recording
            tools. There is nothing here that follows you to another website.
          </div>
          <p>
            The application does use your browser's <strong>local storage</strong> — a small area
            of your own device — for things the software cannot work without:
          </p>
          <ul>
            <li>keeping you signed in so you are not re-prompted on every page,</li>
            <li>remembering your board layout, view preferences, and shop settings,</li>
            <li>holding work in progress so a dropped connection in a steel building does not lose it.</li>
          </ul>
          <p>
            None of it is used to profile you or track you across sites, and none of it leaves your
            device except as needed to run the features above. Clearing your browser's site data
            removes it and signs you out.
          </p>
          <p>
            Because we do not track users, there is no behavioral advertising to opt out of, and we
            have no "Do Not Track" behavior to change.
          </p>
        </Section>

        <Section n="4" title="How we use information">
          <p>We use the information described above only to:</p>
          <ul>
            <li>operate the software and show a shop its own jobs, customers, and records;</li>
            <li>generate estimates, invoices, and reports the shop asks for;</li>
            <li>process the shop's subscription payment;</li>
            <li>respond to support requests;</li>
            <li>maintain security, prevent abuse, and diagnose faults;</li>
            <li>comply with law.</li>
          </ul>
          <p>
            <strong>We do not sell personal information, and we do not share it for advertising or
            cross-context behavioral advertising.</strong> We do not use shop data to build
            marketing profiles, and we do not disclose one shop's data to another shop.
          </p>
        </Section>

        <Section n="5" title="AI features">
          <p>
            Some features — the Command Center shop summary and voice-to-estimate drafting — send
            the relevant repair-order text to Anthropic's API to generate a summary or a draft
            estimate, then return the result to your screen. Requests are sent from our server, and
            the amount of text sent is capped.
          </p>
          <p>
            Anthropic does not use data submitted through its commercial API to train its models.
            We do not send photos, signatures, or payment records to any AI service. If you would
            rather no job text leave the platform this way, do not use these features.
          </p>
        </Section>

        <Section n="6" title="Who else handles the data">
          <p>
            We use a small number of established vendors to run the service. Each receives only
            what it needs, and none of them are permitted to use the data for their own purposes.
          </p>
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr><th>Provider</th><th>What it does</th><th>What it sees</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Amazon Web Services</td>
                  <td>Hosting, database, file storage, sign-in</td>
                  <td>All stored shop data and photos</td>
                </tr>
                <tr>
                  <td>Vercel</td>
                  <td>Delivers the web application</td>
                  <td>Request logs, IP address</td>
                </tr>
                <tr>
                  <td>Stripe</td>
                  <td>Subscription payments</td>
                  <td>Billing contact and card details, entered directly on Stripe's own checkout page</td>
                </tr>
                <tr>
                  <td>Anthropic</td>
                  <td>AI summaries and estimate drafts</td>
                  <td>Repair-order text, only when those features are used</td>
                </tr>
                <tr>
                  <td>OpenStreetMap</td>
                  <td>Map imagery on the dispatch map</td>
                  <td>The map area being viewed, and IP address</td>
                </tr>
                <tr>
                  <td>Google Fonts, unpkg</td>
                  <td>Fonts and a stylesheet</td>
                  <td>IP address when the page loads. Neither sets cookies.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Card payments go directly to Stripe on Stripe's own hosted checkout page. Card numbers
            never pass through {COMPANY}'s servers or browser code.
          </p>
          <p>
            We will also disclose information if required by valid legal process, or where necessary
            to protect the rights and safety of {COMPANY}, our shops, or the public. If a business is
            sold or merged, data may transfer as part of that transaction, subject to this policy.
          </p>
        </Section>

        <Section n="7" title="Messages sent to your customers">
          <p>
            When a shop texts or emails an estimate approval link, that message is composed and sent
            from the staff member's own phone or mail program. {COMPANY} does not operate a texting
            service and does not send messages on a shop's behalf. Shops are responsible for having
            permission to contact their own customers.
          </p>
        </Section>

        <Section n="8" title="How long we keep it">
          <p>
            Shop operating data is kept for as long as the shop's account is active, because repair
            history is the shop's business record and shops need to look back at prior work on a
            vehicle. If a shop closes its account and asks in writing, we will delete or return its
            data within 30 days. Copies in routine encrypted backups age out within 90 days after
            that. We may retain what tax or accounting law requires us to keep.
          </p>
        </Section>

        <Section n="9" title="Security">
          <p>
            Traffic is encrypted in transit with TLS, and stored data and uploaded photos sit in
            access-controlled AWS services encrypted at rest. Accounts are created by shop
            administrators rather than open sign-up, and access is limited by role. No system is
            perfectly secure, but we do not store the two categories that cause the most damage when
            leaked: card numbers and government ID numbers.
          </p>
        </Section>

        <Section n="10" title="Your rights">
          <p>
            You can ask us to tell you what personal information we hold about you, correct it, or
            delete it. Write to <a href={`mailto:${CONTACT}`}>{CONTACT}</a> and we will respond
            within 45 days.
          </p>
          <p>
            If your information is in {COMPANY} because a repair shop put it there, ask that shop
            first — they control that record, and we act on their instruction. Tell us who the shop
            is and we will pass the request along.
          </p>
          <p>
            California residents: we do not sell or share personal information as those terms are
            defined by the CCPA, we do not use or disclose sensitive personal information beyond
            providing the service, and we offer no financial incentive in exchange for personal
            information. Exercising any right here will never cause you to be treated differently.
          </p>
        </Section>

        <Section n="11" title="Where data is stored">
          <p>
            {COMPANY} runs in the United States and is intended for shops operating in the United
            States. If you access it from elsewhere, your information will be processed in the U.S.
          </p>
        </Section>

        <Section n="12" title="Children">
          <p>
            {COMPANY} is business software and is not directed to anyone under 16. We do not
            knowingly collect information from children. If you believe a child's information
            reached us, write to us and we will remove it.
          </p>
        </Section>

        <Section n="13" title="Changes">
          <p>
            If this policy changes we will post the updated version here with a new effective date.
            If a change materially affects how we handle shop data, we will notify account
            administrators by email.
          </p>
        </Section>

        <Section n="14" title="Contact">
          <p>
            Questions, requests, or complaints about privacy go to{' '}
            <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
          </p>
        </Section>

      </main>

      <footer className="pp-footer">
        <div className="pp-footer-logo">REK<span>ON</span></div>
        <div>Real-time operations for truck repair.</div>
        <div><a href={`mailto:${CONTACT}`}>{CONTACT}</a></div>
      </footer>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="pp-section">
      <h2><span className="pp-section-n">{n}</span>{title}</h2>
      {children}
    </section>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const CSS = `
  .pp { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1F2937; background: #fff; line-height: 1.7; }
  .pp *, .pp *::before, .pp *::after { box-sizing: border-box; }
  .pp a { color: #0B2265; font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }

  /* NAV */
  .pp-nav { background: #0B2265; padding: 0 5%; display: flex; align-items: center; justify-content: space-between; height: 64px; position: sticky; top: 0; z-index: 100; }
  .pp-nav-logo { font-size: 22px; font-weight: 800; letter-spacing: 0.12em; color: #fff; cursor: pointer; }
  .pp-nav-logo span { color: #CEA046; }
  .pp-nav-back { background: transparent; color: #fff; font-weight: 600; font-size: 14px; padding: 9px 18px; border-radius: 6px; border: 1.5px solid rgba(255,255,255,0.4); cursor: pointer; transition: border-color 0.15s; }
  .pp-nav-back:hover { border-color: rgba(255,255,255,0.8); }

  /* HEAD */
  .pp-head { background: #0B2265; color: #fff; padding: 56px 5% 64px; }
  .pp-head-label { font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #CEA046; margin-bottom: 12px; }
  .pp-head h1 { font-size: clamp(30px,4.5vw,46px); font-weight: 800; letter-spacing: -0.02em; margin: 0 0 12px; line-height: 1.15; }
  .pp-eff { color: rgba(255,255,255,0.65); font-size: 15px; margin: 0; }

  /* BODY */
  .pp-body { max-width: 760px; margin: 0 auto; padding: 56px 5% 72px; }
  .pp-body p { font-size: 16px; color: #374151; margin: 0 0 16px; }
  .pp-body ul { margin: 0 0 16px; padding-left: 22px; }
  .pp-body li { font-size: 16px; color: #374151; margin-bottom: 10px; }
  .pp-body strong { color: #1F2937; }
  .pp-body em { font-style: italic; }

  .pp-summary { background: #F0F4FF; border-left: 3px solid #CEA046; border-radius: 0 8px 8px 0; padding: 20px 24px; font-size: 16px; color: #374151; margin-bottom: 48px; }

  .pp-callout { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 10px; padding: 18px 22px; font-size: 16px; color: #374151; margin-bottom: 16px; }

  .pp-section { margin-bottom: 44px; }
  .pp-section h2 { font-size: 21px; font-weight: 800; color: #0B2265; letter-spacing: -0.01em; margin: 0 0 16px; display: flex; align-items: baseline; gap: 12px; }
  .pp-section-n { font-size: 13px; font-weight: 800; color: #CEA046; flex-shrink: 0; min-width: 20px; }
  .pp-section h3 { font-size: 15px; font-weight: 700; color: #1F2937; margin: 24px 0 8px; }

  /* TABLE */
  .pp-table-wrap { overflow-x: auto; margin: 0 0 16px; }
  .pp-table { width: 100%; border-collapse: collapse; font-size: 14px; min-width: 520px; }
  .pp-table th { text-align: left; background: #0B2265; color: #fff; font-weight: 700; font-size: 12px; letter-spacing: 0.06em; text-transform: uppercase; padding: 10px 14px; }
  .pp-table td { padding: 12px 14px; border-bottom: 1px solid #E5E7EB; color: #374151; vertical-align: top; }
  .pp-table tr:last-child td { border-bottom: none; }

  /* FOOTER */
  .pp-footer { background: #060f2e; color: rgba(255,255,255,0.5); padding: 32px 5%; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; font-size: 13px; }
  .pp-footer-logo { font-size: 16px; font-weight: 800; letter-spacing: 0.12em; color: #fff; }
  .pp-footer-logo span { color: #CEA046; }
  .pp-footer a { color: rgba(255,255,255,0.5); font-weight: 400; }
  .pp-footer a:hover { color: #CEA046; }

  @media (max-width: 600px) {
    .pp-nav { padding: 0 4%; }
    .pp-head { padding: 40px 4% 48px; }
    .pp-body { padding: 40px 4% 56px; }
    .pp-section h2 { font-size: 19px; }
    .pp-footer { flex-direction: column; text-align: center; }
  }
`;
